import { Fragment, useRef, useState, type ReactNode } from "react";
import { ArrowsClockwise, FileArrowUp, SignOut, X } from "@phosphor-icons/react";
import { RESUME_ACCEPT, type ProfileView } from "../../shared/types.ts";
import { Button, Input, PlacePicker, Select, Steps, cn } from "../ui/index.ts";
import { errorText } from "../lib/api.ts";
import { useApp } from "../lib/ctx.ts";
import { FAMILIES, num, SENIORITY, WORKPLACE } from "../lib/format.ts";
import { uploadResume } from "../lib/supabase.ts";
import { Pill } from "./parts.tsx";
import { Row } from "./settings/parts.tsx";

export type Edit = { p: ProfileView; set: (fn: (p: ProfileView) => ProfileView) => void };

/** Renders the chosen rows in the given order; every row when none are chosen. */
function pick<K extends string>(all: Record<K, ReactNode>, rows?: readonly K[]) {
  return (rows ?? (Object.keys(all) as K[])).map((k) => <Fragment key={k}>{all[k]}</Fragment>);
}

export type ContactRow = "firstName" | "lastName" | "email" | "phone" | "place" | "pronouns" | "linkedin" | "github" | "website";

export function ContactFields({ p, set, rows }: Edit & { rows?: readonly ContactRow[] }) {
  const text = (k: "firstName" | "lastName" | "email" | "phone", type: string, auto: string) => (
    <Input type={type} autoComplete={auto} value={p[k]} onChange={(e) => set((x) => ({ ...x, [k]: e.target.value }))} />
  );
  const place = (k: "city" | "state" | "postal", v: string) => set((x) => ({ ...x, address: { ...x.address, [k]: v } }));
  const link = (k: "linkedin" | "github" | "website", placeholder: string) => (
    <Input type="url" placeholder={placeholder} value={p.links[k] ?? ""} onChange={(e) => set((x) => ({ ...x, links: { ...x.links, [k]: e.target.value } }))} />
  );
  return pick<ContactRow>(
    {
      firstName: <Row label="First name">{text("firstName", "text", "given-name")}</Row>,
      lastName: <Row label="Last name">{text("lastName", "text", "family-name")}</Row>,
      email: (
        <Row label="Email" hint="Where employers reach you.">
          {text("email", "email", "email")}
        </Row>
      ),
      phone: <Row label="Phone">{text("phone", "tel", "tel")}</Row>,
      place: (
        <Row label="Where you live" hint="For forms that ask where you're based.">
          <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,6.5rem)_minmax(0,6.5rem)] gap-2">
            <Input aria-label="City" placeholder="City" autoComplete="address-level2" value={p.address?.city ?? ""} onChange={(e) => place("city", e.target.value)} />
            <Input aria-label="State" placeholder="State" autoComplete="address-level1" value={p.address?.state ?? ""} onChange={(e) => place("state", e.target.value)} />
            <Input aria-label="ZIP" placeholder="ZIP" autoComplete="postal-code" value={p.address?.postal ?? ""} onChange={(e) => place("postal", e.target.value)} />
          </div>
        </Row>
      ),
      pronouns: (
        <Row label="Pronouns" hint="Optional. Sent only when an application asks.">
          <Input placeholder="she/her" value={p.pronouns ?? ""} onChange={(e) => set((x) => ({ ...x, pronouns: e.target.value }))} />
        </Row>
      ),
      linkedin: <Row label="LinkedIn">{link("linkedin", "linkedin.com/in/you")}</Row>,
      github: (
        <Row label="GitHub" hint="Optional.">
          {link("github", "github.com/you")}
        </Row>
      ),
      website: (
        <Row label="Website" hint="Optional.">
          {link("website", "you.com")}
        </Row>
      ),
    },
    rows,
  );
}

type School = ProfileView["education"][number];

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** A month and year kept as "2026-06", or "2026" when only the year is known ("-06" while the year is still blank). */
function MonthYear({ label, value = "", onChange }: { label: string; value?: string; onChange: (v: string) => void }) {
  const year = value.match(/^\d{0,4}/)![0];
  const month = value.match(/-(\d{2})$/)?.[1] ?? "";
  const emit = (y: string, m: string) => onChange(m ? `${y}-${m}` : y);
  return (
    <div className="grid grid-cols-[minmax(0,9rem)_minmax(0,7rem)] gap-2">
      <Select aria-label={`${label} month`} value={month} onChange={(e) => emit(year, e.target.value)}>
        <option value="">Month</option>
        {MONTHS.map((m, i) => (
          <option key={m} value={String(i + 1).padStart(2, "0")}>
            {m}
          </option>
        ))}
      </Select>
      <Input aria-label={`${label} year`} inputMode="numeric" maxLength={4} placeholder="Year" value={year} onChange={(e) => emit(e.target.value.replace(/\D/g, "").slice(0, 4), month)} />
    </div>
  );
}

export function EducationFields({ p, set }: Edit) {
  const school = p.education[0] ?? { school: "" };
  const edu = (k: keyof School) => (e: { target: { value: string } }) =>
    set((x) => ({ ...x, education: [{ ...school, [k]: e.target.value }, ...x.education.slice(1)] }));
  return (
    <>
      <Row label="School">
        <Input placeholder="Your school or university" value={school.school} onChange={edu("school")} />
      </Row>
      <Row label="Degree">
        <Input placeholder="Bachelor's" value={school.degree ?? ""} onChange={edu("degree")} />
      </Row>
      <Row label="Field of study">
        <Input placeholder="Computer Science" value={school.field ?? ""} onChange={edu("field")} />
      </Row>
      <Row label="Started">
        <MonthYear label="Start" value={school.start} onChange={(v) => edu("start")({ target: { value: v } })} />
      </Row>
      <Row label="Graduation" hint="Or when you expect to.">
        <MonthYear label="Graduation" value={school.end} onChange={(v) => edu("end")({ target: { value: v } })} />
      </Row>
    </>
  );
}

export function Resume({ name, onDone }: { name: string | null; onDone: (p: ProfileView) => void }) {
  const app = useApp();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);

  const upload = async (file: File | undefined) => {
    if (!file) return;
    setBusy(true);
    try {
      const p = await uploadResume(file);
      app.setProfile(p);
      onDone(p);
      app.toast("Resume saved.", "ok");
    } catch (e) {
      const code = (e as Error).message;
      app.toast(code === "bad_type" ? "Use a PDF, Word, RTF, ODT or text file." : errorText(e, "The upload didn't go through. Try again."), "bad");
    } finally {
      setBusy(false);
      if (input.current) input.current.value = "";
    }
  };

  return (
    <div
      className={cn(
        "flex min-h-20 items-center justify-between gap-4 rounded-card border border-dashed px-4 py-3 transition-[background-color,border-color] duration-150",
        over ? "border-primary bg-primary-soft" : name ? "border-line-strong bg-sunk/60" : "border-bad/40 bg-tone-red/40",
      )}
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        upload(e.dataTransfer.files[0]);
      }}
    >
      <input ref={input} type="file" accept={RESUME_ACCEPT} hidden onChange={(e) => upload(e.target.files?.[0])} />
      {busy ? (
        <Steps steps={[{ label: "Uploading your resume", state: "active" }]} />
      ) : name ? (
        <Steps steps={[{ label: name, state: "done" }]} />
      ) : (
        <span className="text-sm font-semibold text-bad-ink">Drop your resume here. PDF, Word or text.</span>
      )}
      <Button
        size="sm"
        variant={name ? "secondary" : "primary"}
        icon={name ? <ArrowsClockwise weight="bold" /> : <FileArrowUp weight="bold" />}
        onClick={() => input.current?.click()}
        disabled={busy}
      >
        {name ? "Replace" : "Add your resume"}
      </Button>
    </div>
  );
}

/** With `limit`, only the first options and any already picked show until the user asks for the rest. */
function PillGroup<T extends string>({ value, options, onChange, limit }: { value: T[]; options: Record<string, string>; onChange: (v: T[]) => void; limit?: number }) {
  const [all, setAll] = useState(!limit);
  const entries = Object.entries(options);
  const shown = all ? entries : entries.filter(([k], i) => i < limit! || value.includes(k as T));
  return (
    <div className="flex flex-wrap gap-2">
      {shown.map(([k, label]) => {
        const on = value.includes(k as T);
        return (
          <Pill key={k} on={on} onClick={() => onChange(on ? value.filter((v) => v !== k) : [...value, k as T])}>
            {label}
          </Pill>
        );
      })}
      {shown.length < entries.length && (
        <Pill on={false} onClick={() => setAll(true)} className="text-muted">
          More
        </Pill>
      )}
    </div>
  );
}

function Tags({ value, onChange, placeholder }: { value: string[]; onChange: (v: string[]) => void; placeholder: string }) {
  const [text, setText] = useState("");
  const add = (raw = text) => {
    const t = raw.trim().replace(/,$/, "").trim();
    if (t && !value.some((v) => v.toLowerCase() === t.toLowerCase())) onChange([...value, t]);
    setText("");
  };
  return (
    <div className="flex min-h-10 flex-wrap items-center gap-1.5 rounded-control border border-line-strong bg-surface px-1.5 py-1 transition-[border-color,box-shadow] duration-150 hover:border-ink/25 focus-within:border-primary focus-within:ring-3 focus-within:ring-primary/15">
      {value.map((v) => (
        <button
          key={v}
          type="button"
          onClick={() => onChange(value.filter((x) => x !== v))}
          aria-label={`Remove ${v}`}
          className="inline-flex h-7 items-center gap-1 rounded-chip bg-tone-brand pl-2.5 pr-1.5 text-[13px] font-semibold text-tone-brand-ink transition-[background-color] duration-150 hover:bg-primary-tint"
        >
          {v}
          <X size={14} weight="bold" aria-hidden />
        </button>
      ))}
      <input
        value={text}
        placeholder={value.length ? "Add another" : placeholder}
        onChange={(e) => (e.target.value.endsWith(",") ? add(e.target.value) : setText(e.target.value))}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            add();
          } else if (e.key === "Backspace" && !text && value.length) onChange(value.slice(0, -1));
        }}
        onBlur={() => add()}
        className="h-8 min-w-32 flex-1 bg-transparent px-1.5 text-[15px] text-ink placeholder:text-muted/80 focus:outline-none"
      />
    </div>
  );
}

/** Neither pill is on while `value` is unset. */
function YesNo({ value, onChange }: { value: boolean | undefined; onChange: (v: boolean) => void }) {
  return (
    <div className="flex gap-2">
      <Pill on={value === true} onClick={() => onChange(true)}>
        Yes
      </Pill>
      <Pill on={value === false} onClick={() => onChange(false)}>
        No
      </Pill>
    </div>
  );
}

export type PrefRow = "titles" | "families" | "seniority" | "workplace" | "locations" | "minSalary" | "sponsorship";

/** `short` trims long option lists to the common picks behind a More pill. */
export function PrefFields({ p, set, rows, short }: Edit & { rows?: readonly PrefRow[]; short?: boolean }) {
  const prefs = <K extends keyof ProfileView["prefs"]>(k: K, v: ProfileView["prefs"][K]) => set((x) => ({ ...x, prefs: { ...x.prefs, [k]: v } }));
  return pick<PrefRow>(
    {
      titles: (
        <Row label="Roles">
          <Tags value={p.prefs.titles} onChange={(v) => prefs("titles", v)} placeholder="Software engineer, product designer" />
        </Row>
      ),
      families: (
        <Row label="Fields">
          <PillGroup value={p.prefs.families} options={FAMILIES} onChange={(v) => prefs("families", v)} limit={short ? 8 : undefined} />
        </Row>
      ),
      seniority: (
        <Row label="Level">
          <PillGroup value={p.prefs.seniority} options={SENIORITY} onChange={(v) => prefs("seniority", v)} />
        </Row>
      ),
      workplace: (
        <Row label="Where">
          <PillGroup value={p.prefs.workplace} options={WORKPLACE} onChange={(v) => prefs("workplace", v)} />
        </Row>
      ),
      locations: (
        <Row label="Locations">
          <PlacePicker value={p.prefs.locations} onChange={(v) => prefs("locations", v)} />
        </Row>
      ),
      minSalary: (
        <Row label="Minimum pay per year">
          <Input
            inputMode="numeric"
            placeholder="$120,000"
            className="tabular-nums"
            value={p.prefs.minSalary ? `$${num(p.prefs.minSalary)}` : ""}
            onChange={(e) => {
              const n = Number(e.target.value.replace(/[^\d]/g, ""));
              prefs("minSalary", n > 0 ? n : undefined);
            }}
          />
        </Row>
      ),
      sponsorship: (
        <Row label="Do you need visa sponsorship?">
          <YesNo value={p.workAuth.needsSponsorship} onChange={(v) => set((x) => ({ ...x, workAuth: { ...x.workAuth, needsSponsorship: v } }))} />
        </Row>
      ),
    },
    rows,
  );
}

export type AnswerRow = "authorized" | "relocate" | "startDate" | "salary" | "citizenship" | "convicted" | "formerEmployee";

export function AnswerFields({ p, set, rows }: Edit & { rows?: readonly AnswerRow[] }) {
  const attest = (k: keyof ProfileView["attestations"]) => (v: boolean) => set((x) => ({ ...x, attestations: { ...x.attestations, [k]: v } }));
  return pick<AnswerRow>(
    {
      authorized: (
        <Row label="Are you authorized to work in the country of the job?">
          <YesNo value={p.workAuth.authorizedToWork} onChange={(v) => set((x) => ({ ...x, workAuth: { ...x.workAuth, authorizedToWork: v } }))} />
        </Row>
      ),
      relocate: (
        <Row label="Are you willing to relocate?">
          <YesNo value={p.relocation.willing} onChange={(v) => set((x) => ({ ...x, relocation: { ...x.relocation, willing: v } }))} />
        </Row>
      ),
      startDate: (
        <Row label="When can you start?">
          <Input placeholder="In two weeks" value={p.startDate ?? ""} onChange={(e) => set((x) => ({ ...x, startDate: e.target.value }))} />
        </Row>
      ),
      salary: (
        <Row label="Expected pay">
          <Input placeholder="$150,000" value={p.salaryExpectation ?? ""} onChange={(e) => set((x) => ({ ...x, salaryExpectation: e.target.value }))} />
        </Row>
      ),
      citizenship: (
        <Row label="Citizenship" hint="Some employers, like Amazon and defense companies, ask.">
          <Input
            placeholder="United States"
            value={p.workAuth.citizenship ?? ""}
            onChange={(e) => set((x) => ({ ...x, workAuth: { ...x.workAuth, citizenship: e.target.value } }))}
          />
        </Row>
      ),
      convicted: (
        <Row label="Have you been convicted of a felony?">
          <YesNo value={p.attestations.convicted} onChange={attest("convicted")} />
        </Row>
      ),
      formerEmployee: (
        <Row label="Have you ever worked at a company you might apply to?">
          <YesNo value={p.attestations.formerEmployee} onChange={attest("formerEmployee")} />
        </Row>
      ),
    },
    rows,
  );
}

export function AccountFields() {
  const app = useApp();
  return (
    <>
      <Row label="Sign-in email">
        <Input value={app.user.email} readOnly disabled />
      </Row>
      <Row label="Sign out" hint="Signs you out on this device.">
        <Button variant="secondary" icon={<SignOut weight="bold" />} onClick={app.signOut}>
          Sign out
        </Button>
      </Row>
    </>
  );
}
